import React from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, Upload, Building2, Plus, X } from 'lucide-react';
import { registrationApi } from '../../lib/api';

/** Formulaire public (aucune authentification) rempli par un futur client à partir du lien reçu par
 *  email — cf. bouton "Envoyer un formulaire d'inscription" du superadmin (AdminClientsView). Le jeton
 *  dans l'URL identifie la demande côté serveur ; il n'y a pas de session/JWT ici. */
export const ClientRegistrationForm: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [phase, setPhase] = React.useState<'loading' | 'form' | 'submitted' | 'invalid' | 'expired' | 'already'>('loading');
    const [clientType, setClientType] = React.useState<'restauration' | 'prestation'>('restauration');
    const [locationMode, setLocationMode] = React.useState<'domicile' | 'entreprise' | 'les_deux'>('domicile');

    const [fullName, setFullName] = React.useState('');
    const [companyName, setCompanyName] = React.useState('');
    const [contactEmail, setContactEmail] = React.useState('');
    const [username, setUsername] = React.useState('');
    const [logoUrl, setLogoUrl] = React.useState('');
    const [logoFile, setLogoFile] = React.useState<File | null>(null);
    const [uploadingLogo, setUploadingLogo] = React.useState(false);
    const [tvaRates, setTvaRates] = React.useState<number[]>([20]);
    const [newTvaInput, setNewTvaInput] = React.useState('');
    const [address, setAddress] = React.useState('');
    const [postalCode, setPostalCode] = React.useState('');
    const [city, setCity] = React.useState('');
    const [country, setCountry] = React.useState('France');
    const [siret, setSiret] = React.useState('');
    const [tva, setTva] = React.useState('');
    const [phone, setPhone] = React.useState('');
    const [capital, setCapital] = React.useState('');
    const [ape, setApe] = React.useState('');
    const [siegeSocial, setSiegeSocial] = React.useState('');
    const [rcsVille, setRcsVille] = React.useState('');
    const [rcsNumero, setRcsNumero] = React.useState('');

    const [error, setError] = React.useState('');
    const [submitting, setSubmitting] = React.useState(false);

    React.useEffect(() => {
        if (!token) { setPhase('invalid'); return; }
        registrationApi.getForm(token).then((data: any) => {
            if (data?.status === 'expired') { setPhase('expired'); return; }
            if (data?.status && data.status !== 'invited') { setPhase('already'); return; }
            setClientType(data?.client_type === 'prestation' ? 'prestation' : 'restauration');
            setPhase('form');
        }).catch(() => setPhase('invalid'));
    }, [token]);

    const handleLogoChange = async (file: File | null) => {
        setLogoFile(file);
        if (!file || !token) return;
        setUploadingLogo(true);
        setError('');
        try {
            const base64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const result = `${reader.result ?? ''}`;
                    resolve(result.includes(',') ? result.split(',')[1] : result);
                };
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
            const upload: any = await registrationApi.uploadLogo(token, base64, file.type || 'image/png');
            if (upload?.logo_url) setLogoUrl(upload.logo_url);
            else setError(upload?.error || 'Erreur lors du téléversement du logo');
        } catch (e: any) {
            setError(e?.message || 'Erreur lors du téléversement du logo');
        } finally {
            setUploadingLogo(false);
        }
    };

    const addTvaRate = () => {
        const v = parseFloat(newTvaInput);
        if (!Number.isFinite(v) || v < 0 || tvaRates.includes(v)) return;
        setTvaRates([...tvaRates, v].sort((a, b) => a - b));
        setNewTvaInput('');
    };
    const removeTvaRate = (v: number) => setTvaRates(tvaRates.filter((r) => r !== v));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token) return;
        setError('');
        if (!fullName.trim() || !companyName.trim() || !contactEmail.trim() || !username.trim()) {
            setError('Merci de remplir tous les champs obligatoires.');
            return;
        }
        setSubmitting(true);
        try {
            await registrationApi.submit(token, {
                full_name: fullName, company_name: companyName, contact_email: contactEmail, username,
                logo_url: logoUrl || null, tva_rates: tvaRates,
                address, postal_code: postalCode, city, country, siret, tva, phone,
                capital, ape, siege_social: siegeSocial, rcs_ville: rcsVille, rcs_numero: rcsNumero,
                ...(clientType === 'prestation' ? { prestation_location_mode: locationMode } : {}),
            });
            setPhase('submitted');
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'envoi de la demande");
        } finally {
            setSubmitting(false);
        }
    };

    const inputClass = "w-full bg-surface border border-border rounded-lg px-3 py-2 text-content text-sm outline-none focus:border-brand-500 transition-colors";
    const labelClass = "block text-xs font-bold text-content-muted uppercase mb-1";

    if (phase === 'loading') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-surface-app">
                <Loader2 className="animate-spin text-content-muted" size={32} />
            </div>
        );
    }

    if (phase === 'invalid' || phase === 'expired' || phase === 'already') {
        const messages = {
            invalid: { icon: <XCircle className="text-red-500" size={40} />, title: 'Lien invalide', text: "Ce lien d'inscription n'existe pas." },
            expired: { icon: <XCircle className="text-red-500" size={40} />, title: 'Lien expiré', text: "Ce lien d'inscription n'est plus valide. Merci de contacter L'IAmani pour en recevoir un nouveau." },
            already: { icon: <CheckCircle2 className="text-brand-500" size={40} />, title: 'Demande déjà traitée', text: 'Cette demande d\'inscription a déjà été soumise et traitée.' },
        } as const;
        const m = messages[phase];
        return (
            <div className="min-h-screen flex items-center justify-center bg-surface-app p-4">
                <div className="bg-surface border border-border rounded-2xl p-10 max-w-md w-full text-center space-y-4">
                    <div className="flex justify-center">{m.icon}</div>
                    <h1 className="text-xl font-black text-content">{m.title}</h1>
                    <p className="text-sm text-content-muted">{m.text}</p>
                </div>
            </div>
        );
    }

    if (phase === 'submitted') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-surface-app p-4">
                <div className="bg-surface border border-border rounded-2xl p-10 max-w-md w-full text-center space-y-4">
                    <div className="flex justify-center"><CheckCircle2 className="text-green-500" size={40} /></div>
                    <h1 className="text-xl font-black text-content">Demande envoyée</h1>
                    <p className="text-sm text-content-muted">Votre demande d'inscription a bien été transmise à L'IAmani. Vous recevrez un email dès qu'elle aura été traitée.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-surface-app py-10 px-4">
            <div className="max-w-2xl mx-auto">
                <div className="flex items-center gap-3 mb-8 justify-center">
                    <div className="w-12 h-12 rounded-2xl bg-brand-500/15 flex items-center justify-center text-brand-500">
                        <Building2 size={22} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-black text-content">Demande d'inscription</h1>
                        <p className="text-sm text-content-muted capitalize">Client {clientType === 'prestation' ? 'Prestation de service' : 'Restauration / Hôtellerie'}</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="bg-surface border border-border rounded-2xl p-8 space-y-6">
                    {error && <div className="bg-red-500/10 border border-red-500/30 text-red-500 text-sm rounded-lg px-3 py-2">{error}</div>}

                    <div>
                        <h2 className="text-xs font-black uppercase tracking-wider text-content-muted mb-3">Informations générales</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className={labelClass}>Nom complet *</label>
                                <input value={fullName} onChange={(e) => setFullName(e.target.value)} className={inputClass} placeholder="Prénom Nom" required />
                            </div>
                            <div>
                                <label className={labelClass}>Nom de l'établissement *</label>
                                <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} className={inputClass} required />
                            </div>
                            <div>
                                <label className={labelClass}>Email *</label>
                                <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className={inputClass} required />
                            </div>
                            <div>
                                <label className={labelClass}>Identifiant (username) *</label>
                                <input value={username} onChange={(e) => setUsername(e.target.value.toLowerCase())} className={inputClass} required />
                            </div>
                        </div>
                    </div>

                    {clientType === 'prestation' && (
                        <div>
                            <label className={labelClass}>Mode d'intervention</label>
                            <div className="grid grid-cols-3 gap-2">
                                {([
                                    { key: 'domicile', label: 'Chez le client' },
                                    { key: 'entreprise', label: "Dans l'entreprise" },
                                    { key: 'les_deux', label: 'Les deux' },
                                ] as const).map((opt) => (
                                    <button key={opt.key} type="button" onClick={() => setLocationMode(opt.key)}
                                        className={`px-3 py-2.5 rounded-lg text-xs font-bold border transition-all ${locationMode === opt.key ? 'border-brand-500/40 bg-brand-500/10 text-brand-600 dark:text-brand-400' : 'border-border text-content-muted'}`}>
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div>
                        <label className={labelClass}>Logo de l'établissement</label>
                        <div className="flex items-center gap-3">
                            <div className="w-16 h-16 rounded-xl border border-border bg-surface-soft flex items-center justify-center overflow-hidden shrink-0">
                                {logoUrl ? <img src={logoUrl} alt="" className="w-full h-full object-cover" /> : <Upload size={18} className="text-content-muted" />}
                            </div>
                            <label className="cursor-pointer px-4 py-2 rounded-lg border border-border bg-surface-soft text-content text-sm font-semibold hover:border-brand-500">
                                {uploadingLogo ? 'Envoi...' : logoFile ? logoFile.name : 'Choisir un fichier'}
                                <input type="file" accept="image/png,image/jpeg,image/gif" className="hidden" onChange={(e) => handleLogoChange(e.target.files?.[0] || null)} disabled={uploadingLogo} />
                            </label>
                        </div>
                        <p className="text-[11px] text-content-muted mt-1">Formats acceptés : PNG, JPG, GIF (max 1 Mo).</p>
                    </div>

                    <div>
                        <label className={labelClass}>Taux de TVA appliqués</label>
                        <div className="flex flex-wrap gap-2 mb-2">
                            {tvaRates.map((r) => (
                                <span key={r} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-surface-soft border border-border text-content text-sm">
                                    {r}%
                                    <button type="button" onClick={() => removeTvaRate(r)} className="text-content-muted hover:text-red-500"><X size={12} /></button>
                                </span>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <input value={newTvaInput} onChange={(e) => setNewTvaInput(e.target.value)} type="number" className={`${inputClass} flex-1`} placeholder="Ex: 10" />
                            <button type="button" onClick={addTvaRate} className="px-3 py-2 rounded-lg border border-border bg-surface-soft text-content text-sm font-semibold hover:border-brand-500 inline-flex items-center gap-1"><Plus size={14} /> Ajouter</button>
                        </div>
                    </div>

                    <div>
                        <h2 className="text-xs font-black uppercase tracking-wider text-content-muted mb-3">Informations légales</h2>
                        <div className="space-y-4">
                            <div>
                                <label className={labelClass}>Rue / Adresse postale</label>
                                <input value={address} onChange={(e) => setAddress(e.target.value)} className={inputClass} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Code Postal</label>
                                    <input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Ville</label>
                                    <input value={city} onChange={(e) => setCity(e.target.value)} className={inputClass} />
                                </div>
                            </div>
                            <div>
                                <label className={labelClass}>Pays</label>
                                <input value={country} onChange={(e) => setCountry(e.target.value)} className={inputClass} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>SIRET</label>
                                    <input value={siret} onChange={(e) => setSiret(e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>TVA Intracommunautaire</label>
                                    <input value={tva} onChange={(e) => setTva(e.target.value)} className={inputClass} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Téléphone</label>
                                    <input value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Capital Social</label>
                                    <input value={capital} onChange={(e) => setCapital(e.target.value)} className={inputClass} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Code APE / NAF</label>
                                    <input value={ape} onChange={(e) => setApe(e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Siège Social (si différent)</label>
                                    <input value={siegeSocial} onChange={(e) => setSiegeSocial(e.target.value)} className={inputClass} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Ville d'immatriculation RCS</label>
                                    <input value={rcsVille} onChange={(e) => setRcsVille(e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Numéro RCS</label>
                                    <input value={rcsNumero} onChange={(e) => setRcsNumero(e.target.value)} className={inputClass} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <button type="submit" disabled={submitting || uploadingLogo}
                        className="w-full py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold shadow-lg disabled:opacity-50">
                        {submitting ? 'Envoi...' : 'Envoyer la demande'}
                    </button>
                </form>
            </div>
        </div>
    );
};
